import * as React from 'react'
import cn from 'classnames'
import { partition } from 'lodash-es'
import { DotsThreeVertical } from '@phosphor-icons/react'
import styles from './ChatSidebar.module.scss'
import { useWfiContext } from './WfiContext'

export type ChatSidebarProps = {
  onSwitchChat: (chatId: number | null, orgSnapshotId: number | null) => void
}

export const ChatSidebar: React.VFC<ChatSidebarProps> = ({ onSwitchChat }) => {
  const { chats, currentChatId, deleteChat, starChat, unstarChat } =
    useWfiContext()
  const [starredChats, recentChats] = partition(chats, (chat) => chat.starred)

  return (
    <aside className={styles.container}>
      <h2 className={styles.heading}>Workforce Intelligence</h2>
      {currentChatId != null && (
        <button
          className={styles.newChat}
          onClick={() => onSwitchChat(null, null)}
        >
          New Chat
        </button>
      )}
      <h3>Starred projects</h3>
      <ul>
        {starredChats.map((chat) => (
          <ChatItem
            key={chat.id}
            description={chat.description}
            selected={chat.id === currentChatId}
            onClick={() => onSwitchChat(chat.id, chat.orgSnapshotId)}
            onDelete={() => deleteChat(chat.id)}
            onUnstar={() => unstarChat(chat.id)}
          />
        ))}
      </ul>

      <h3>Recent chats</h3>
      <ul>
        {recentChats.map((chat) => (
          <ChatItem
            key={chat.id}
            description={chat.description}
            selected={chat.id === currentChatId}
            onClick={() => onSwitchChat(chat.id, chat.orgSnapshotId)}
            onDelete={() => deleteChat(chat.id)}
            onStar={() => starChat(chat.id)}
          />
        ))}
      </ul>
    </aside>
  )
}

type ChatItemProps = {
  description: string
  onClick: () => void
  onDelete: () => void
  onStar?: () => void
  selected?: boolean
  onUnstar?: () => void
}

const ChatItem: React.VFC<ChatItemProps> = ({
  description,
  selected = false,
  onClick,
  onDelete,
  onStar,
  onUnstar,
}) => (
  <li className={cn(styles.chatItem, selected && styles.selected)}>
    <button onClick={onClick}>
      <p>{description}</p>
    </button>
    <Hamburger onDelete={onDelete} onStar={onStar} onUnstar={onUnstar} />
  </li>
)

type HamburgerProps = {
  onDelete: () => void
  onStar?: () => void
  onUnstar?: () => void
}

const Hamburger: React.VFC<HamburgerProps> = ({
  onDelete,
  onStar,
  onUnstar,
}) => {
  const [open, setOpen] = React.useState(false)

  const toggle = () => setOpen((o) => !o)

  return (
    <div className={styles.hamburgerContainer}>
      <button className={styles.hamburgerButton} onClick={toggle}>
        <DotsThreeVertical height="20px" width="20px" />
      </button>
      {open && (
        <>
          <HamburgerMenu
            onDelete={onDelete}
            onStar={onStar}
            onUnstar={onUnstar}
          />
          <div
            className={styles.hamburgerOverlay}
            onClick={() => setOpen(false)}
          ></div>
        </>
      )}
    </div>
  )
}

const HamburgerMenu: React.VFC<HamburgerProps> = ({
  onStar,
  onUnstar,
  onDelete,
}) => (
  <div className={styles.hamburgerMenu}>
    {onStar && <button onClick={onStar}>Star Project</button>}
    {onUnstar && <button onClick={onUnstar}>Unstar Project</button>}
    <button onClick={onDelete}>Delete</button>
  </div>
)
